/**
 * Moderation console — the report queue + takedown (SAFE-6).
 *
 * Staff (admin / super_admin) review reported posts & Broadcasts and act:
 *   - Take down   → worker sets removed_* + deleted_at (content offline)
 *   - Dismiss     → worker clears the auto-deprioritization; content stays
 *
 * Triaged by reason priority: terrorism / CSAM (critical) sort to the top
 * regardless of raw report volume — the "bad actors broadcasting bad acts"
 * lever. Every action is audited worker-side (moderation_actions) and
 * attributed to the staff member's Cloudflare Access email.
 *
 * Destructive actions use a two-step "click again to confirm" (no
 * window.confirm — same restraint as the main app, §0c).
 */

import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { getReportReason, parseServerDate, reportReasonsFor } from '@postopia/shared';
import { AdminShell } from '../components/AdminShell';
import {
  moderationApi,
  ModerationError,
  type QueueItem,
  type ReportPriority,
  type QueueMedia,
  type QueueParams,
  type QueueReport,
  type EntityKind,
  type MessageQueueItem,
  type FeedbackQueueItem,
  type MessageReportKind,
  type EntityQueueItem,
} from '../lib/moderationApi';
import { entryMediaUrl } from '../lib/contestsApi';

export const Route = createFileRoute('/moderation')({
  component: ModerationRoute,
});

function ModerationRoute() {
  return (
    <AdminShell mode="feature" featureName="Moderation">
      <ModerationConsole />
    </AdminShell>
  );
}

const PRIORITY_STYLE: Record<
  ReportPriority,
  { label: string; bg: string; fg: string }
> = {
  critical: { label: 'URGENT', bg: '#F4D9D2', fg: '#9A2A12' },
  high: { label: 'High', bg: '#F5E4CF', fg: '#8A5A1A' },
  normal: { label: 'Normal', bg: 'var(--c-bg-surface-alt)', fg: 'var(--c-fg-secondary)' },
};

type Pending = { postId: string; action: 'takedown' | 'dismiss' } | null;

type Filters = Required<Omit<QueueParams, 'q' | 'reason' | 'from' | 'to'>> & {
  q: string;
  reason: string;
  from: string;
  to: string;
  /** Client-side — priority is derived from the reason set in JS. */
  priority: ReportPriority | '';
  /** Client-side — only posts with at least one flagged reporter. */
  reporter: '' | 'flagged';
};

const DEFAULT_FILTERS: Filters = {
  q: '',
  status: 'open',
  removed: '',
  kind: '',
  reason: '',
  priority: '',
  reporter: '',
  date: 'reported',
  from: '',
  to: '',
  sort: 'reports',
};

type Lightbox = { media: QueueMedia[]; index: number } | null;

function ModerationConsole() {
  /** Which report lane is showing. Posts is the incumbent rich queue;
   *  Users / Communities are the 0301 lanes — the reports that were
   *  accepted-but-invisible until 2026-08-30. */
  const [lane, setLane] = useState<'post' | EntityKind | 'message' | 'feedback'>('post');
  const [queue, setQueue] = useState<QueueItem[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [lightbox, setLightbox] = useState<Lightbox>(null);

  const load = useCallback(async (f: Filters) => {
    setError(null);
    try {
      const { priority: _p, reporter: _r, ...params } = f;
      const res = await moderationApi.queue(params);
      setQueue(res.queue);
      setTotal(res.total);
    } catch (err) {
      if (err instanceof ModerationError && err.status === 403) {
        setError('Your account does not have moderation access.');
      } else if (err instanceof ModerationError && err.code === 'moderation_not_configured') {
        setError('Moderation is not configured on this environment yet.');
      } else {
        setError("Couldn't load the queue. Try again.");
      }
      setQueue([]);
    }
  }, []);

  // Debounced: typing in the search box refetches after a pause; selects
  // and dates land on the same timer (cheap, and one code path).
  useEffect(() => {
    const t = setTimeout(() => void load(filters), 250);
    return () => clearTimeout(t);
  }, [filters, load]);

  const act = useCallback(
    async (item: QueueItem, action: 'takedown' | 'dismiss') => {
      if (!pending || pending.postId !== item.post_id || pending.action !== action) {
        setPending({ postId: item.post_id, action });
        return;
      }
      setPending(null);
      setBusyId(item.post_id);
      setStatus(null);
      try {
        if (action === 'takedown') {
          const reason = topReasonCode(item.reasons);
          await moderationApi.takedown(item.post_id, { reason });
          setStatus(`Took ${labelFor(item)} offline.`);
        } else {
          await moderationApi.dismiss(item.post_id, {});
          setStatus(`Dismissed reports on ${labelFor(item)}.`);
        }
        // The open queue drops the row immediately; any other view (taken
        // down / dismissed / all) re-reads so the row's new state shows.
        if (filters.status === 'open') {
          setQueue((q) => (q ? q.filter((x) => x.post_id !== item.post_id) : q));
        }
        void load(filters);
      } catch {
        setStatus("Action failed. Try again.");
      } finally {
        setBusyId(null);
      }
    },
    [pending, filters, load],
  );

  // Sensitive (0530): one tap, no arm-then-confirm (it is reversible and hides nothing).
  const toggleSensitive = useCallback(
    async (item: QueueItem) => {
      const next = !item.is_sensitive;
      setBusyId(item.post_id);
      setStatus(null);
      try {
        await moderationApi.setSensitive(item.post_id, next);
        setQueue((q) => (q ? q.map((x) => (x.post_id === item.post_id ? { ...x, is_sensitive: next } : x)) : q));
        setStatus(next ? `Marked ${labelFor(item)} sensitive.` : `Removed the sensitive cover from ${labelFor(item)}.`);
      } catch {
        setStatus('Action failed. Try again.');
      } finally {
        setBusyId(null);
      }
    },
    [],
  );

  const visible = queue
    ? queue.filter(
        (i) =>
          (!filters.priority || i.priority === filters.priority) &&
          (filters.reporter !== 'flagged' || i.suspect_reporters > 0),
      )
    : null;

  const reporterAction = useCallback(
    async (r: QueueReport, mode: 'suspend7d' | 'suspend' | 'unsuspend') => {
      if (!r.reporter_id) return;
      setStatus(null);
      try {
        const who = r.reporter_handle ? `@${r.reporter_handle}` : 'the reporter';
        if (mode === 'unsuspend') {
          await moderationApi.unsuspendUser(r.reporter_id);
          setStatus(`Lifted the suspension on ${who}.`);
        } else {
          await moderationApi.suspendUser(r.reporter_id, {
            ...(mode === 'suspend' ? { permanent: true } : { days: 7 }),
            reason: 'Spam reporting',
          });
          setStatus(mode === 'suspend' ? `Suspended ${who} permanently.` : `Suspended ${who} for 7 days.`);
        }
        void load(filters);
      } catch {
        setStatus('Account action failed. Try again.');
      }
    },
    [filters, load],
  );
  const filtered = JSON.stringify(filters) !== JSON.stringify(DEFAULT_FILTERS);

  return (
    <main
      style={{
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        background: 'var(--c-bg-page)',
        padding: '40px 24px',
        overflowY: 'auto',
      }}
    >
      <div style={{ maxWidth: 760, width: '100%' }}>
        <header style={{ marginBottom: 16 }}>
          <h1
            style={{
              fontFamily: '"Source Serif 4", Georgia, serif',
              fontWeight: 600,
              fontSize: 28,
              letterSpacing: '-0.01em',
              margin: 0,
              color: 'var(--c-fg-default)',
            }}
          >
            Report{' '}
            <em style={{ fontStyle: 'italic', fontWeight: 500, color: 'var(--c-accent-default)' }}>
              queue
            </em>
            .
          </h1>
          <p
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: 13,
              color: 'var(--c-fg-secondary)',
              marginTop: 4,
              marginBottom: 0,
            }}
          >
            Reported posts, Broadcasts &amp; Vibes. A report flags content; only a
            takedown removes it.
          </p>
        </header>
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          {(['post', 'user', 'community', 'message', 'chat_group', 'contest_entry', 'contest', 'store', 'listing', 'feedback'] as const).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setLane(k)}
              style={{
                padding: '6px 14px',
                borderRadius: 999,
                border: '1px solid var(--c-border-hairline)',
                background: lane === k ? 'var(--c-fg-default)' : 'var(--c-bg-surface)',
                color: lane === k ? 'var(--c-bg-page)' : 'var(--c-fg-default)',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {k === 'post'
                ? 'Posts'
                : k === 'user'
                  ? 'Users'
                  : k === 'community'
                    ? 'Communities'
                    : k === 'message'
                      ? 'Messages'
                      : k === 'chat_group'
                        ? 'Group chats'
                        : k === 'contest_entry'
                        ? 'Stage entries'
                        : k === 'store'
                          ? 'Stores'
                          : k === 'listing'
                            ? 'Shop items'
                            : k === 'feedback'
                              ? 'Feedback'
                              : 'Contests'}
            </button>
          ))}
        </div>
        {lane === 'message' ? (
          <MessageQueue />
        ) : lane === 'feedback' ? (
          <FeedbackQueue />
        ) : lane !== 'post' ? (
          <EntityQueue kind={lane} />
        ) : (
        <>
        <FilterBar
          filters={filters}
          onChange={setFilters}
          onReset={() => setFilters(DEFAULT_FILTERS)}
          filtered={filtered}
          shown={visible?.length ?? 0}
          total={total}
        />
        {status ? (
          <div
            role="status"
            style={{
              marginBottom: 12,
              padding: '8px 12px',
              fontFamily: 'Inter, sans-serif',
              fontSize: 13,
              color: 'var(--c-fg-default)',
              background: 'var(--c-bg-surface-alt)',
              border: '1px solid var(--c-border-hairline)',
              borderRadius: 8,
            }}
          >
            {status}
          </div>
        ) : null}
        {error ? (
          <div
            role="alert"
            style={{
              padding: '12px 14px',
              fontFamily: 'Inter, sans-serif',
              fontSize: 13,
              color: '#9A2A12',
              background: '#F4D9D2',
              borderRadius: 8,
            }}
          >
            {error}
          </div>
        ) : visible === null ? (
          <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: 'var(--c-fg-tertiary)' }}>
            Loading…
          </p>
        ) : visible.length === 0 ? (
          <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 14, color: 'var(--c-fg-secondary)' }}>
            {filtered ? 'Nothing matches these filters.' : 'Nothing in the queue. Reported content will appear here.'}
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {visible.map((item) => (
              <QueueRow
                key={item.post_id}
                item={item}
                pending={pending}
                busy={busyId === item.post_id}
                onAct={act}
                onSensitive={toggleSensitive}
                onOpenMedia={(media, index) => setLightbox({ media, index })}
                onReporterAction={reporterAction}
              />
            ))}
          </div>
        )}
      </>
        )}
      </div>
      {lightbox ? (
        <MediaLightbox
          media={lightbox.media}
          index={lightbox.index}
          onIndex={(index) => setLightbox({ media: lightbox.media, index })}
          onClose={() => setLightbox(null)}
        />
      ) : null}
    </main>
  );
}

const FIELD: CSSProperties = {
  fontFamily: 'Inter, sans-serif',
  fontSize: 13,
  padding: '7px 10px',
  borderRadius: 8,
  border: '1px solid var(--c-border-hairline)',
  background: 'var(--c-bg-surface)',
  color: 'var(--c-fg-default)',
  minWidth: 0,
};

function Select<T extends string>({
  value,
  onChange,
  options,
  label,
}: {
  value: T;
  onChange: (v: T) => void;
  options: Array<[T, string]>;
  label: string;
}) {
  return (
    <select aria-label={label} value={value} onChange={(e) => onChange(e.target.value as T)} style={FIELD}>
      {options.map(([v, l]) => (
        <option key={v} value={v}>
          {l}
        </option>
      ))}
    </select>
  );
}

/** Search + filters. Everything except priority is resolved server-side. */
function FilterBar({
  filters,
  onChange,
  onReset,
  filtered,
  shown,
  total,
}: {
  filters: Filters;
  onChange: (f: Filters) => void;
  onReset: () => void;
  filtered: boolean;
  shown: number;
  total: number;
}) {
  const set = <K extends keyof Filters>(k: K, v: Filters[K]) => onChange({ ...filters, [k]: v });
  return (
    <div
      style={{
        marginBottom: 14,
        padding: 12,
        background: 'var(--c-bg-surface)',
        border: '1px solid var(--c-border-hairline)',
        borderRadius: 8,
        display: 'grid',
        gap: 8,
      }}
    >
      <input
        type="search"
        value={filters.q}
        onChange={(e) => set('q', e.target.value)}
        placeholder="Search body, headline, author, reporter, report details, or a post id"
        aria-label="Search reports"
        style={{ ...FIELD, width: '100%', boxSizing: 'border-box' }}
      />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <Select
          label="Report status"
          value={filters.status}
          onChange={(v) => set('status', v)}
          options={[
            ['open', 'Open reports'],
            ['actioned', 'Actioned (taken down)'],
            ['dismissed', 'Dismissed'],
            ['all', 'All reports'],
          ]}
        />
        <Select
          label="Post state"
          value={filters.removed}
          onChange={(v) => set('removed', v)}
          options={[
            ['', 'Any post state'],
            ['yes', 'Taken down'],
            ['no', 'Still live'],
          ]}
        />
        <Select
          label="Kind"
          value={filters.kind}
          onChange={(v) => set('kind', v)}
          options={[
            ['', 'Any kind'],
            ['post', 'Posts'],
            ['broadcast', 'Broadcasts'],
            ['vibe', 'Vibes'],
          ]}
        />
        <Select
          label="Reason"
          value={filters.reason}
          onChange={(v) => set('reason', v)}
          options={[['', 'Any reason'], ...reportReasonsFor('post').map((r): [string, string] => [r.code, r.label])]}
        />
        <Select
          label="Priority"
          value={filters.priority}
          onChange={(v) => set('priority', v)}
          options={[
            ['', 'Any priority'],
            ['critical', 'Urgent'],
            ['high', 'High'],
            ['normal', 'Normal'],
          ]}
        />
        <Select
          label="Reporter"
          value={filters.reporter}
          onChange={(v) => set('reporter', v)}
          options={[
            ['', 'Any reporter'],
            ['flagged', 'Flagged reporters only'],
          ]}
        />
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
        <Select
          label="Date field"
          value={filters.date}
          onChange={(v) => set('date', v)}
          options={[
            ['reported', 'Reported between'],
            ['posted', 'Posted between'],
          ]}
        />
        <input type="date" aria-label="From" value={filters.from} max={filters.to || undefined} onChange={(e) => set('from', e.target.value)} style={FIELD} />
        <span style={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: 'var(--c-fg-tertiary)' }}>to</span>
        <input type="date" aria-label="To" value={filters.to} min={filters.from || undefined} onChange={(e) => set('to', e.target.value)} style={FIELD} />
        <Select
          label="Sort"
          value={filters.sort}
          onChange={(v) => set('sort', v)}
          options={[
            ['reports', 'Most reported first'],
            ['newest', 'Newest report first'],
            ['oldest', 'Oldest report first'],
            ['posted', 'Newest post first'],
          ]}
        />
        <span
          style={{
            marginLeft: 'auto',
            fontFamily: 'Inter, sans-serif',
            fontSize: 12,
            color: 'var(--c-fg-tertiary)',
          }}
        >
          {shown === total ? `${total} post${total === 1 ? '' : 's'}` : `${shown} of ${total} posts`}
        </span>
        {filtered ? (
          <button
            type="button"
            onClick={onReset}
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: 12,
              fontWeight: 600,
              padding: '6px 10px',
              borderRadius: 8,
              border: '1px solid var(--c-border-strong)',
              background: 'transparent',
              color: 'var(--c-fg-default)',
              cursor: 'pointer',
            }}
          >
            Reset
          </button>
        ) : null}
      </div>
    </div>
  );
}

/** Full-screen viewer for a post's media: ← → to page, Esc to close. */
function MediaLightbox({
  media,
  index,
  onIndex,
  onClose,
}: {
  media: QueueMedia[];
  index: number;
  onIndex: (i: number) => void;
  onClose: () => void;
}) {
  const m = media[index];
  const prev = () => onIndex((index - 1 + media.length) % media.length);
  const next = () => onIndex((index + 1) % media.length);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft' && media.length > 1) prev();
      else if (e.key === 'ArrowRight' && media.length > 1) next();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });
  if (!m) return null;
  const isImage = m.kind === 'image' || m.kind === 'gif';
  const navBtn: CSSProperties = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    width: 44,
    height: 44,
    borderRadius: 999,
    border: '1px solid rgba(250,247,242,0.35)',
    background: 'rgba(14,13,12,0.6)',
    color: '#FAF7F2',
    fontSize: 20,
    cursor: 'pointer',
  };
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Media viewer"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'rgba(14,13,12,0.92)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 48,
      }}
    >
      <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: '100%', maxHeight: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
        {isImage && m.url ? (
          <img
            src={m.url}
            alt={m.alt_text ?? ''}
            style={{ maxWidth: 'calc(100vw - 96px)', maxHeight: 'calc(100vh - 140px)', objectFit: 'contain', display: 'block' }}
          />
        ) : m.kind === 'video' && m.url ? (
          <video
            key={m.id}
            src={m.url}
            poster={m.poster_url ?? undefined}
            controls
            autoPlay
            playsInline
            style={{ maxWidth: 'calc(100vw - 96px)', maxHeight: 'calc(100vh - 140px)', display: 'block', background: '#000' }}
          />
        ) : m.kind === 'audio' && m.url ? (
          <audio key={m.id} src={m.url} controls autoPlay style={{ width: 'min(640px, calc(100vw - 96px))' }} />
        ) : (
          <span style={{ color: '#FAF7F2', fontFamily: 'Inter, sans-serif', fontSize: 13 }}>
            No preview for this {m.kind}.
          </span>
        )}
        <div
          style={{
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            fontFamily: 'Inter, sans-serif',
            fontSize: 12,
            color: 'rgba(250,247,242,0.8)',
          }}
        >
          {media.length > 1 ? <span>{index + 1} / {media.length}</span> : null}
          <span>
            {m.kind}{m.width && m.height ? ` · ${m.width}×${m.height}` : ''}{m.duration_ms ? ` · ${Math.round(m.duration_ms / 1000)}s` : ''}
          </span>
          {m.alt_text ? <span>· {m.alt_text}</span> : null}
          {m.url ? (
            <a href={m.url} target="_blank" rel="noreferrer" style={{ color: '#FAF7F2', fontWeight: 600 }}>
              Open original ↗
            </a>
          ) : null}
        </div>
      </div>
      <button type="button" aria-label="Close" onClick={onClose} style={{ ...navBtn, top: 16, right: 16, transform: 'none' }}>
        ×
      </button>
      {media.length > 1 ? (
        <>
          <button type="button" aria-label="Previous" onClick={(e) => { e.stopPropagation(); prev(); }} style={{ ...navBtn, left: 16 }}>
            ‹
          </button>
          <button type="button" aria-label="Next" onClick={(e) => { e.stopPropagation(); next(); }} style={{ ...navBtn, right: 16 }}>
            ›
          </button>
        </>
      ) : null}
    </div>
  );
}

function QueueRow({
  item,
  pending,
  busy,
  onAct,
  onSensitive,
  onOpenMedia,
  onReporterAction,
}: {
  item: QueueItem;
  pending: Pending;
  busy: boolean;
  onAct: (item: QueueItem, action: 'takedown' | 'dismiss') => void;
  onSensitive: (item: QueueItem) => void;
  onOpenMedia: (media: QueueMedia[], index: number) => void;
  onReporterAction: (r: QueueReport, mode: 'suspend7d' | 'suspend' | 'unsuspend') => void;
}) {
  const pr = PRIORITY_STYLE[item.priority];
  // Two-step confirm for account actions, keyed per report + mode.
  const [armed, setArmed] = useState<string | null>(null);
  const reporterBtn = (r: QueueReport, mode: 'suspend7d' | 'suspend' | 'unsuspend', label: string) => {
    const key = `${r.id}:${mode}`;
    const isArmed = armed === key;
    const danger = mode !== 'unsuspend';
    return (
      <button
        type="button"
        onClick={() => {
          if (!isArmed) {
            setArmed(key);
            return;
          }
          setArmed(null);
          onReporterAction(r, mode);
        }}
        onBlur={() => setArmed((a) => (a === key ? null : a))}
        style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: 11,
          fontWeight: 600,
          padding: '3px 8px',
          borderRadius: 6,
          border: `1px solid ${danger ? '#C7563E' : 'var(--c-border-strong)'}`,
          background: isArmed ? (danger ? '#9A2A12' : 'var(--c-bg-surface-alt)') : 'transparent',
          color: isArmed && danger ? '#FAF7F2' : danger ? '#9A2A12' : 'var(--c-fg-default)',
          cursor: 'pointer',
        }}
      >
        {isArmed ? `Confirm: ${label}` : label}
      </button>
    );
  };
  const armedTakedown =
    pending?.postId === item.post_id && pending.action === 'takedown';
  const armedDismiss =
    pending?.postId === item.post_id && pending.action === 'dismiss';

  return (
    <div
      style={{
        padding: 16,
        background: 'var(--c-bg-surface)',
        border: '1px solid var(--c-border-hairline)',
        borderRadius: 8,
        borderLeft: item.priority === 'critical' ? '3px solid #9A2A12' : undefined,
      }}
    >
      {/* Top row: priority + type + counts */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span
          style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            padding: '2px 7px',
            borderRadius: 999,
            background: pr.bg,
            color: pr.fg,
          }}
        >
          {pr.label}
        </span>
        <span
          style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--c-fg-secondary)',
          }}
        >
          {KIND_LABEL[item.kind] ?? (item.is_article ? 'Broadcast' : 'Post')}
        </span>
        <span style={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: 'var(--c-fg-tertiary)' }}>
          {item.open_reports} report{item.open_reports === 1 ? '' : 's'}
          {item.report_count > item.open_reports ? ` · ${item.report_count} total` : ''}
        </span>
        {item.removed ? (
          <span style={{ fontFamily: 'Inter, sans-serif', fontSize: 11, color: '#9A2A12', fontWeight: 600 }}>
            already removed
          </span>
        ) : null}
        {item.suspect_reporters > 0 ? (
          <Flag tone="warn">
            {item.suspect_reporters} flagged reporter{item.suspect_reporters === 1 ? '' : 's'}
          </Flag>
        ) : null}
      </div>

      {/* Author */}
      <div
        style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: 13,
          color: 'var(--c-fg-default)',
          marginTop: 8,
        }}
      >
        <a
          href={item.author_profile_url}
          target="_blank"
          rel="noreferrer"
          style={{ color: 'inherit', textDecoration: 'none' }}
        >
          <strong style={{ fontWeight: 600 }}>{item.author_display_name}</strong>{' '}
          <span style={{ color: 'var(--c-fg-tertiary)' }}>@{item.author_handle}</span>
        </a>
        <span style={{ color: 'var(--c-fg-tertiary)' }}>
          {' · '}joined {fmtDate(item.author_created_at)} ({ago(item.author_created_at)})
        </span>
        {item.author_verifications.length > 0 ? (
          <span style={{ color: '#5C7A3D' }}>
            {' · '}verified: {item.author_verifications.map(verificationLabel).join(', ')}
          </span>
        ) : null}
        <span
          style={{
            color: item.author_prior_takedowns > 0 ? '#9A2A12' : 'var(--c-fg-tertiary)',
            fontWeight: item.author_prior_takedowns > 0 ? 600 : 400,
          }}
        >
          {' · '}{item.author_prior_takedowns} prior takedown{item.author_prior_takedowns === 1 ? '' : 's'}
        </span>
        {item.author_other_reported_posts > 0 ? (
          <span style={{ color: '#9A2A12' }}>
            {' · '}{item.author_other_reported_posts} other reported post{item.author_other_reported_posts === 1 ? '' : 's'}
          </span>
        ) : null}
      </div>
      {/* Post facts: when, where, audience, reach, disclosures. */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '4px 10px',
          marginTop: 6,
          fontFamily: 'Inter, sans-serif',
          fontSize: 12,
          color: 'var(--c-fg-tertiary)',
        }}
      >
        <span>posted {fmtDate(item.created_at)} ({ago(item.created_at)})</span>
        <span>{item.visibility}</span>
        {item.community_slug ? <span>in /c/{item.community_slug}</span> : null}
        <span>
          {item.likes_count} sparks · {item.replies_count} replies · {item.reposts_count} echoes · {item.views_count} views
        </span>
        {item.is_mature ? <Flag tone="warn">Mature</Flag> : null}
        {item.is_sensitive ? <Flag tone="warn">Sensitive</Flag> : null}
        {item.is_ai_generated ? <Flag>AI-made</Flag> : null}
        {item.is_link_post ? <Flag>Link post</Flag> : null}
        {item.removed && item.removed_reason ? <Flag tone="warn">removed: {item.removed_reason}</Flag> : null}
        <a
          href={item.site_url}
          target="_blank"
          rel="noreferrer"
          style={{ color: 'var(--c-fg-link)', fontWeight: 600, marginLeft: 'auto' }}
        >
          Open on site ↗
        </a>
      </div>

      {/* Headline (Broadcast) + excerpt */}
      {item.is_article && item.article_headline ? (
        <div
          style={{
            fontFamily: '"Source Serif 4", Georgia, serif',
            fontSize: 15,
            fontWeight: 600,
            color: 'var(--c-fg-default)',
            marginTop: 4,
          }}
        >
          {item.article_headline}
        </div>
      ) : null}
      {item.content ? <FullBody text={item.content} /> : null}
      {item.media.length > 0 ? <MediaStrip media={item.media} onOpen={(i) => onOpenMedia(item.media, i)} /> : null}
      {item.link_url ? (
        <a
          href={item.link_url}
          target="_blank"
          rel="noreferrer"
          style={{
            display: 'block',
            marginTop: 8,
            padding: '8px 10px',
            border: '1px solid var(--c-border-hairline)',
            borderRadius: 6,
            fontFamily: 'Inter, sans-serif',
            fontSize: 12,
            color: 'var(--c-fg-default)',
            textDecoration: 'none',
          }}
        >
          <div style={{ fontWeight: 600 }}>{item.link_title ?? item.link_url}</div>
          <div style={{ color: 'var(--c-fg-tertiary)', wordBreak: 'break-all' }}>{item.link_url}</div>
        </a>
      ) : null}
      {item.quoted ? (
        <div
          style={{
            marginTop: 8,
            padding: '8px 10px',
            borderLeft: '2px solid var(--c-border-strong)',
            fontFamily: 'Inter, sans-serif',
            fontSize: 12,
            color: 'var(--c-fg-secondary)',
          }}
        >
          <span style={{ fontWeight: 600, color: 'var(--c-fg-default)' }}>
            Quoting @{item.quoted.handle}
          </span>
          {' — '}
          <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{item.quoted.excerpt}</span>
        </div>
      ) : null}

      {/* Reason chips */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
        {item.reasons.map((code) => (
          <span
            key={code}
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: 11,
              padding: '2px 8px',
              borderRadius: 999,
              background: 'var(--c-bg-surface-alt)',
              color: 'var(--c-fg-secondary)',
              border: '1px solid var(--c-border-hairline)',
            }}
          >
            {getReportReason(code)?.label ?? code}
          </span>
        ))}
      </div>
      {item.reports.length > 0 ? (
        <ul
          style={{
            listStyle: 'none',
            margin: '10px 0 0',
            padding: 0,
            display: 'grid',
            gap: 6,
            fontFamily: 'Inter, sans-serif',
            fontSize: 12,
          }}
        >
          {item.reports.map((r) => (
            <li
              key={r.id}
              style={{
                padding: '6px 10px',
                background: 'var(--c-bg-surface-alt)',
                borderRadius: 6,
                color: 'var(--c-fg-secondary)',
              }}
            >
              <span style={{ fontWeight: 600, color: 'var(--c-fg-default)' }}>
                {getReportReason(r.reason)?.label ?? r.reason}
              </span>
              {' · '}
              {r.reporter_handle ? `@${r.reporter_handle}` : 'unknown reporter'}
              {' · '}
              <span title={fmtDate(r.created_at, true)}>{ago(r.created_at)}</span>
              {r.reporter_stats ? (
                <span style={{ color: 'var(--c-fg-tertiary)' }}>
                  {' · '}
                  {r.reporter_stats.total} report{r.reporter_stats.total === 1 ? '' : 's'} all-time
                  {' ('}
                  {r.reporter_stats.actioned} actioned · {r.reporter_stats.dismissed} dismissed · {r.reporter_stats.open} open
                  {')'}
                  {r.reporter_stats.last_24h > 0 ? ` · ${r.reporter_stats.last_24h} in 24h` : ''}
                  {r.reporter_stats.same_author_30d > 1 ? ` · ${r.reporter_stats.same_author_30d} on this author in 30d` : ''}
                </span>
              ) : null}
              {r.reporter_flags.length > 0 || r.reporter_suspended ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginTop: 5 }}>
                  {r.reporter_suspended ? <Flag>Reporter suspended</Flag> : null}
                  {r.reporter_flags.map((f) => (
                    <Flag key={f} tone="warn">
                      {REPORTER_FLAG_LABEL[f] ?? f}
                    </Flag>
                  ))}
                  {r.reporter_id
                    ? r.reporter_suspended
                      ? reporterBtn(r, 'unsuspend', 'Unsuspend reporter')
                      : (
                        <>
                          {reporterBtn(r, 'suspend7d', 'Suspend reporter 7d')}
                          {reporterBtn(r, 'suspend', 'Suspend permanently')}
                        </>
                      )
                    : null}
                </div>
              ) : null}
              {r.details ? (
                <div
                  style={{
                    marginTop: 3,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    color: 'var(--c-fg-default)',
                  }}
                >
                  “{r.details}”
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button
          onClick={() => onAct(item, 'takedown')}
          disabled={busy}
          style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 13,
            fontWeight: 600,
            padding: '8px 14px',
            borderRadius: 8,
            border: 'none',
            cursor: busy ? 'default' : 'pointer',
            background: armedTakedown ? '#9A2A12' : '#C7563E',
            color: '#FAF7F2',
            opacity: busy ? 0.6 : 1,
          }}
        >
          {busy ? 'Working…' : armedTakedown ? 'Confirm takedown' : 'Take down'}
        </button>
        <button
          onClick={() => onAct(item, 'dismiss')}
          disabled={busy}
          style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 13,
            fontWeight: 600,
            padding: '8px 14px',
            borderRadius: 8,
            border: '1px solid var(--c-border-strong)',
            cursor: busy ? 'default' : 'pointer',
            background: armedDismiss ? 'var(--c-bg-surface-alt)' : 'transparent',
            color: 'var(--c-fg-default)',
            opacity: busy ? 0.6 : 1,
          }}
        >
          {armedDismiss ? 'Confirm dismiss' : 'Dismiss'}
        </button>
        {/* Viewer discretion (0530): cover the media instead of taking it down. */}
        <button
          onClick={() => onSensitive(item)}
          disabled={busy}
          style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 13,
            fontWeight: 600,
            padding: '8px 14px',
            borderRadius: 8,
            border: '1px solid var(--c-border-strong)',
            cursor: busy ? 'default' : 'pointer',
            background: 'transparent',
            color: 'var(--c-fg-default)',
            opacity: busy ? 0.6 : 1,
          }}
        >
          {item.is_sensitive ? 'Remove sensitive cover' : 'Mark sensitive'}
        </button>
      </div>
    </div>
  );
}

const KIND_LABEL: Record<string, string> = { post: 'Post', broadcast: 'Broadcast', vibe: 'Vibe' };

// Spam-reporter signals (computed in the worker; see reporterFlags there).
const REPORTER_FLAG_LABEL: Record<string, string> = {
  high_volume: 'Possible spam: high volume',
  mostly_dismissed: 'Possible spam: mostly dismissed',
  targets_author: 'Possible spam: targets this author',
};

const VERIFICATION_LABEL: Record<string, string> = {
  identity_verified: 'person',
  notable_person: 'notable',
  independent_artist: 'artist',
  business_org: 'org',
  news_organization: 'news org',
  journalist: 'press',
  government_official: 'gov official',
  government_body: 'gov body',
  credentialed_expert: 'expert',
  domain_verified: 'domain',
  community_vouched: 'community',
  news_curator: 'news curator',
};
function verificationLabel(type: string): string {
  return VERIFICATION_LABEL[type] ?? type.replace(/_/g, ' ');
}

function fmtDate(iso: string, withTime = false): string {
  const d = parseServerDate(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return withTime
    ? d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
    : d.toLocaleDateString(undefined, { dateStyle: 'medium' });
}

function ago(iso: string): string {
  const t = parseServerDate(iso).getTime();
  if (Number.isNaN(t)) return '';
  const s = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (s < 60) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 60) return `${d}d ago`;
  const mo = Math.round(d / 30);
  if (mo < 24) return `${mo}mo ago`;
  return `${Math.round(d / 365)}y ago`;
}

function Flag({ children, tone }: { children: ReactNode; tone?: 'warn' }) {
  return (
    <span
      style={{
        fontFamily: 'Inter, sans-serif',
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        padding: '1px 6px',
        borderRadius: 999,
        background: tone === 'warn' ? '#F6E3DE' : 'var(--c-bg-surface-alt)',
        color: tone === 'warn' ? '#9A2A12' : 'var(--c-fg-secondary)',
        border: '1px solid var(--c-border-hairline)',
      }}
    >
      {children}
    </span>
  );
}

const BODY_FOLD_CHARS = 700;

/** The whole post body — folded past ~700 chars with an explicit "Show all". */
function FullBody({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const long = text.length > BODY_FOLD_CHARS;
  const shown = open || !long ? text : text.slice(0, BODY_FOLD_CHARS);
  return (
    <div style={{ marginTop: 6 }}>
      <p
        style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: 13,
          lineHeight: 1.45,
          color: 'var(--c-fg-default)',
          margin: 0,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {shown}
        {long && !open ? '…' : ''}
      </p>
      {long ? (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          style={{
            marginTop: 4,
            padding: 0,
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            fontFamily: 'Inter, sans-serif',
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--c-fg-link)',
          }}
        >
          {open ? 'Show less' : `Show all (${text.length.toLocaleString()} chars)`}
        </button>
      ) : null}
    </div>
  );
}

/** Attached media — opens the lightbox. Video/audio show their poster (or a label). */
function MediaStrip({ media, onOpen }: { media: QueueMedia[]; onOpen: (index: number) => void }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
      {media.map((m, i) => {
        const img = m.kind === 'image' || m.kind === 'gif' ? m.url : m.poster_url;
        return (
          <button
            type="button"
            key={m.id}
            onClick={() => onOpen(i)}
            title={m.alt_text ?? `${m.kind}${m.duration_ms ? ` · ${Math.round(m.duration_ms / 1000)}s` : ''}`}
            style={{
              position: 'relative',
              width: 120,
              height: 120,
              borderRadius: 6,
              overflow: 'hidden',
              background: '#1F1C17',
              border: '1px solid var(--c-border-hairline)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#FAF7F2',
              fontFamily: 'Inter, sans-serif',
              fontSize: 11,
              fontWeight: 600,
              padding: 0,
              cursor: 'zoom-in',
            }}
          >
            {img ? (
              <img
                src={img}
                alt={m.alt_text ?? ''}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            ) : (
              <span>{m.kind}</span>
            )}
            {m.kind !== 'image' && m.kind !== 'gif' ? (
              <span
                style={{
                  position: 'absolute',
                  left: 6,
                  bottom: 6,
                  padding: '1px 6px',
                  borderRadius: 999,
                  background: 'rgba(14,13,12,0.7)',
                  fontSize: 10,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                {m.kind}{m.duration_ms ? ` ${Math.round(m.duration_ms / 1000)}s` : ''}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

const PRIORITY_RANK: Record<ReportPriority, number> = { critical: 3, high: 2, normal: 1 };

/** The gravest reason code among a post's reports → the takedown reason. */
function topReasonCode(codes: string[]): string {
  let top = codes[0] ?? 'policy_violation';
  let rank = -1;
  for (const code of codes) {
    const def = getReportReason(code);
    const r = def ? PRIORITY_RANK[def.priority] : 0;
    if (r > rank) {
      rank = r;
      top = code;
    }
  }
  return top;
}

function labelFor(item: QueueItem): string {
  return item.is_article ? 'this Broadcast' : 'this post';
}


/* ── EntityQueue — the Users / Communities report lanes (0301) ────────────
 * Deliberately leaner than the post queue: identity + the reports + the
 * decision. Enforcement reuses the existing suspend endpoints; resolving
 * here settles the REPORTS. Destructive actions use the §0c two-step
 * (click arms, second click commits) — no window.confirm. */
function EntityQueue({ kind }: { kind: EntityKind }) {
  const [status, setStatus] = useState<'open' | 'actioned' | 'dismissed' | 'all'>('open');
  const [rows, setRows] = useState<EntityQueueItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [armed, setArmed] = useState<string | null>(null); // `${action}:${id}` pending second click

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await moderationApi.entityQueue(kind, status);
      setRows(res.queue);
    } catch {
      setError("Couldn't load the queue. Try again.");
    }
  }, [kind, status]);

  useEffect(() => {
    setRows(null);
    setArmed(null);
    void load();
  }, [load]);

  const act = useCallback(
    async (key: string, fn: () => Promise<unknown>, done: string) => {
      if (armed !== key) {
        setArmed(key);
        return;
      }
      setArmed(null);
      setNote(null);
      try {
        await fn();
        setNote(done);
        void load();
      } catch {
        setNote('Action failed. Try again.');
      }
    },
    [armed, load],
  );

  const webOrigin = 'https://postopia.com';

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <label style={{ fontSize: 13, color: 'var(--c-fg-secondary)' }}>Status</label>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as typeof status)}
          style={{
            padding: '6px 10px',
            borderRadius: 6,
            border: '1px solid var(--c-border-hairline)',
            background: 'var(--c-bg-surface)',
            color: 'var(--c-fg-default)',
            fontSize: 13,
          }}
        >
          <option value="open">Open</option>
          <option value="actioned">Actioned</option>
          <option value="dismissed">Dismissed</option>
          <option value="all">All</option>
        </select>
        {note ? (
          <span style={{ fontSize: 13, color: 'var(--c-fg-secondary)' }}>{note}</span>
        ) : null}
      </div>

      {error ? (
        <p style={{ color: 'var(--c-fg-default)' }}>{error}</p>
      ) : rows === null ? (
        <p style={{ color: 'var(--c-fg-tertiary)', fontSize: 14 }}>Loading…</p>
      ) : rows.length === 0 ? (
        <p style={{ color: 'var(--c-fg-tertiary)', fontSize: 14 }}>
          {kind === 'user'
            ? 'No reported users in this view.'
            : kind === 'community'
              ? 'No reported communities in this view.'
              : kind === 'chat_group'
                ? 'No reported group chats in this view.'
                : kind === 'contest_entry'
                  ? 'No reported Stage entries in this view.'
                  : kind === 'store'
                    ? 'No reported stores in this view.'
                    : kind === 'listing'
                      ? 'No reported Shop items in this view.'
                      : 'No reported contests in this view.'}
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {rows.map((r) => {
            const title =
              kind === 'user'
                ? `@${r.handle ?? r.target_id}${r.display_name ? ` — ${r.display_name}` : ''}`
                : kind === 'community'
                  ? `${r.name ?? r.target_id} (/c/${r.slug ?? '?'})`
                  : kind === 'chat_group'
                    ? `${r.name ?? 'Untitled group'} · ${r.member_count ?? '?'} members · ${r.group_state ?? 'open'}`
                  : kind === 'store'
                    ? `${r.name ?? r.target_id} · store of @${r.handle ?? '?'} · ${r.store_status ?? '?'}`
                  : kind === 'listing'
                    ? `${r.name ?? r.target_id} · ${r.store_name ?? '?'} (@${r.handle ?? '?'}) · ${r.listing_state ?? '?'}`
                  : kind === 'contest_entry'
                    ? `@${r.handle ?? '?'}'s entry in ${r.name ?? r.slug ?? r.target_id} · ${r.entry_status ?? '?'}`
                    : `${r.name ?? r.target_id} (/stage/${r.slug ?? '?'}) · ${r.contest_status ?? '?'}${r.hosted ? ` · hosted by @${r.handle ?? '?'}` : ''}`;
            const href =
              kind === 'user'
                ? `${webOrigin}/profile/${r.handle ?? ''}`
                : kind === 'community'
                  ? `${webOrigin}/c/${r.slug ?? ''}`
                  : kind === 'chat_group'
                    ? `${webOrigin}/profile/${r.handle ?? ''}`
                  : kind === 'store'
                    ? `${webOrigin}/shop/s/${r.handle ?? ''}`
                  : kind === 'listing'
                    ? `${webOrigin}/shop/l/${r.target_id}`
                  : kind === 'contest_entry'
                    ? `${webOrigin}/e/${r.handle ?? ''}/${r.slug ?? ''}`
                    : `${webOrigin}/stage/${r.slug ?? ''}`;
            return (
              <div
                key={r.target_id}
                style={{
                  border: '1px solid var(--c-border-hairline)',
                  borderRadius: 8,
                  background: 'var(--c-bg-surface)',
                  padding: 14,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                  <a
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    style={{ fontWeight: 600, fontSize: 15, color: 'var(--c-fg-default)', textDecoration: 'none' }}
                  >
                    {title}
                  </a>
                  {kind === 'contest_entry' || kind === 'contest' ? (
                    <a href="/stage" style={{ fontSize: 12, color: 'var(--c-fg-link)' }}>
                      {kind === 'contest_entry' ? 'Disqualify in the Stage console' : 'Pause or close in the Stage console'}
                    </a>
                  ) : null}
                  {kind === 'user' && r.is_suspended ? (
                    <span style={{ fontSize: 12, color: 'var(--c-fg-secondary)' }}>suspended</span>
                  ) : null}
                  {kind === 'user' && !r.is_suspended && r.suspended_until ? (
                    <span style={{ fontSize: 12, color: 'var(--c-fg-secondary)' }}>
                      suspended until {parseServerDate(r.suspended_until).toLocaleDateString()}
                    </span>
                  ) : null}
                  <span style={{ fontSize: 12, color: 'var(--c-fg-tertiary)' }}>
                    {r.open_reports} report{r.open_reports === 1 ? '' : 's'} · {r.reasons.join(', ')}
                  </span>
                </div>

                <ul style={{ margin: '10px 0 0', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {r.reports.slice(0, 8).map((rep) => (
                    <li key={rep.id} style={{ fontSize: 13, color: 'var(--c-fg-secondary)' }}>
                      <strong style={{ color: 'var(--c-fg-default)', fontWeight: 600 }}>
                        {rep.reporter_handle ? `@${rep.reporter_handle}` : 'unknown reporter'}
                      </strong>{' '}
                      · {rep.reason}
                      {rep.details ? ` — ${rep.details}` : ''}{' '}
                      <span style={{ color: 'var(--c-fg-tertiary)' }}>
                        · {parseServerDate(rep.created_at).toLocaleString()} · {rep.status}
                      </span>
                    </li>
                  ))}
                  {r.reports.length > 8 ? (
                    <li style={{ fontSize: 12, color: 'var(--c-fg-tertiary)' }}>
                      +{r.reports.length - 8} more
                    </li>
                  ) : null}
                </ul>

                {/* A group chat (0411): the snapshot the latest report took,
                    so a rename or deleted messages cannot hide it. The title
                    link opens the creator's profile (groups have no page). */}
                {kind === 'chat_group' ? <GroupEvidence r={r} /> : null}
                {/* A store or an item (0473): what the reporter saw, kept even if
                    the seller has since edited it. */}
                {kind === 'store' || kind === 'listing' ? <ShopEvidence r={r} kind={kind} /> : null}
                {kind === 'store' || kind === 'listing' ? (
                  <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                    {kind === 'store' && r.store_status !== 'suspended' ? (
                      <LaneButton
                        label="Suspend store"
                        armedLabel="Click again: hide the store and freeze it"
                        isArmed={armed === `stS:${r.target_id}`}
                        onClick={() =>
                          act(
                            `stS:${r.target_id}`,
                            async () => {
                              await moderationApi.moderateStore(r.target_id, 'suspend', `Reported: ${r.reasons.join(', ')}`);
                              await moderationApi.resolveEntity(kind, r.target_id, 'actioned').catch(() => null);
                            },
                            'Store suspended; reports actioned.',
                          )
                        }
                      />
                    ) : null}
                    {kind === 'store' && r.store_status === 'suspended' ? (
                      <LaneButton
                        label="Reinstate store"
                        armedLabel="Click again: back to paused, the owner reopens it"
                        isArmed={armed === `stR:${r.target_id}`}
                        onClick={() => act(`stR:${r.target_id}`, () => moderationApi.moderateStore(r.target_id, 'reinstate'), 'Store reinstated (paused).')}
                      />
                    ) : null}
                    {kind === 'listing' && r.listing_state !== 'removed' ? (
                      <LaneButton
                        label="Remove item"
                        armedLabel="Click again: take the item down"
                        isArmed={armed === `liR:${r.target_id}`}
                        onClick={() =>
                          act(
                            `liR:${r.target_id}`,
                            async () => {
                              await moderationApi.moderateListing(r.target_id, 'remove', `Reported: ${r.reasons.join(', ')}`);
                              await moderationApi.resolveEntity(kind, r.target_id, 'actioned').catch(() => null);
                            },
                            'Item removed; reports actioned.',
                          )
                        }
                      />
                    ) : null}
                    {kind === 'listing' && r.listing_state === 'removed' ? (
                      <LaneButton
                        label="Restore item"
                        armedLabel="Click again: back to paused, the seller republishes"
                        isArmed={armed === `liU:${r.target_id}`}
                        onClick={() => act(`liU:${r.target_id}`, () => moderationApi.moderateListing(r.target_id, 'restore'), 'Item restored (paused).')}
                      />
                    ) : null}
                  </div>
                ) : null}

                {kind === 'chat_group' ? (
                  <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                    {r.group_state === 'open' ? (
                      <LaneButton
                        label="Restrict group"
                        armedLabel="Click again: freeze adds and edits"
                        isArmed={armed === `grpR:${r.target_id}`}
                        onClick={() =>
                          act(
                            `grpR:${r.target_id}`,
                            async () => {
                              await moderationApi.moderateGroup(r.target_id, 'restrict', `Reported: ${r.reasons.join(', ')}`);
                              await moderationApi.resolveEntity(kind, r.target_id, 'actioned').catch(() => null);
                            },
                            'Group restricted; reports actioned.',
                          )
                        }
                      />
                    ) : null}
                    {r.group_state !== 'closed' ? (
                      <LaneButton
                        label="Close group"
                        armedLabel="Click again: nobody can send"
                        isArmed={armed === `grpC:${r.target_id}`}
                        onClick={() =>
                          act(
                            `grpC:${r.target_id}`,
                            async () => {
                              await moderationApi.moderateGroup(r.target_id, 'close', `Reported: ${r.reasons.join(', ')}`);
                              await moderationApi.resolveEntity(kind, r.target_id, 'actioned').catch(() => null);
                            },
                            'Group closed; reports actioned.',
                          )
                        }
                      />
                    ) : null}
                    {r.group_state && r.group_state !== 'open' ? (
                      <LaneButton
                        label="Lift"
                        armedLabel="Click again: back to normal"
                        isArmed={armed === `grpL:${r.target_id}`}
                        onClick={() =>
                          act(`grpL:${r.target_id}`, () => moderationApi.moderateGroup(r.target_id, 'lift'), 'Group back to normal.')
                        }
                      />
                    ) : null}
                  </div>
                ) : null}

                {status === 'open' || status === 'all' ? (
                  <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                    <LaneButton
                      label="Dismiss reports"
                      armedLabel="Click again to dismiss"
                      isArmed={armed === `dismiss:${r.target_id}`}
                      onClick={() =>
                        act(
                          `dismiss:${r.target_id}`,
                          () => moderationApi.resolveEntity(kind, r.target_id, 'dismissed'),
                          'Reports dismissed.',
                        )
                      }
                    />
                    <LaneButton
                      label="Mark actioned"
                      armedLabel="Click again to confirm"
                      isArmed={armed === `actioned:${r.target_id}`}
                      onClick={() =>
                        act(
                          `actioned:${r.target_id}`,
                          () => moderationApi.resolveEntity(kind, r.target_id, 'actioned'),
                          'Marked actioned.',
                        )
                      }
                    />
                    {kind === 'user' && !r.is_suspended ? (
                      <>
                        <LaneButton
                          label="Suspend 7d"
                          armedLabel="Click again — suspend 7 days"
                          isArmed={armed === `sus7:${r.target_id}`}
                          onClick={() =>
                            act(
                              `sus7:${r.target_id}`,
                              async () => {
                                await moderationApi.suspendUser(r.target_id, {
                                  days: 7,
                                  reason: `Reported: ${r.reasons.join(', ')}`,
                                });
                                await moderationApi.resolveEntity(kind, r.target_id, 'actioned');
                              },
                              'Suspended 7 days; reports actioned.',
                            )
                          }
                        />
                        <LaneButton
                          label="Suspend permanently"
                          armedLabel="Click again — PERMANENT"
                          isArmed={armed === `susP:${r.target_id}`}
                          onClick={() =>
                            act(
                              `susP:${r.target_id}`,
                              async () => {
                                await moderationApi.suspendUser(r.target_id, {
                                  permanent: true,
                                  reason: `Reported: ${r.reasons.join(', ')}`,
                                });
                                await moderationApi.resolveEntity(kind, r.target_id, 'actioned');
                              },
                              'Suspended permanently; reports actioned.',
                            )
                          }
                        />
                      </>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** The reported group's snapshot (0411): who runs it, what it is, and its
 *  latest messages at report time. */
/** A reported store or item (0473): the snapshot the latest report took. */
function ShopEvidence({ r, kind }: { r: EntityQueueItem; kind: 'store' | 'listing' }) {
  const ev = r.evidence;
  if (!ev) return null;
  const photos = kind === 'listing' ? (ev.photo_keys ?? []) : [ev.logo_key, ev.banner_key].filter((k): k is string => !!k);
  return (
    <div style={{ marginTop: 10, padding: 10, borderRadius: 6, background: 'var(--c-bg-surface-alt)', fontSize: 13, display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.3, color: 'var(--c-fg-tertiary)' }}>WHAT THE REPORTER SAW</span>
      <strong style={{ fontWeight: 600 }}>{kind === 'listing' ? ev.title : ev.name}</strong>
      {kind === 'listing' && ev.price_minor ? (
        <span style={{ color: 'var(--c-fg-secondary)' }}>
          {ev.price_minor} minor units {ev.currency} · {ev.store_name}
        </span>
      ) : null}
      {kind === 'store' && ev.tagline ? <span style={{ color: 'var(--c-fg-secondary)' }}>{ev.tagline}</span> : null}
      {(kind === 'listing' ? ev.body : ev.about) ? (
        <p style={{ margin: 0, whiteSpace: 'pre-line', color: 'var(--c-fg-default)' }}>{kind === 'listing' ? ev.body : ev.about}</p>
      ) : null}
      {photos.length ? (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {photos.slice(0, 10).map((k) => (
            <a key={k} href={entryMediaUrl(`/media/${k}`) ?? undefined} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: 'var(--c-fg-link)' }}>
              photo
            </a>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function GroupEvidence({ r }: { r: EntityQueueItem }) {
  const ev = r.evidence;
  if (!ev) return null;
  const recent = (ev.recent_messages ?? []).slice(-10);
  return (
    <div
      style={{
        marginTop: 10,
        padding: 10,
        borderRadius: 6,
        background: 'var(--c-bg-surface-alt)',
        fontSize: 13,
        color: 'var(--c-fg-secondary)',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <span>
        Created by {ev.created_by_handle ? `@${ev.created_by_handle}` : 'unknown'}
        {ev.admin_handles && ev.admin_handles.length ? ` · admins ${ev.admin_handles.map((h) => `@${h}`).join(', ')}` : ''}
        {ev.community_slug ? ` · linked to /c/${ev.community_slug}` : ''}
      </span>
      {ev.description ? <span>About: {ev.description}</span> : null}
      {r.moderation_note ? <span>Staff note: {r.moderation_note}</span> : null}
      {recent.length ? (
        <ul style={{ margin: '4px 0 0', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 3 }}>
          {recent.map((m) => (
            <li key={m.id}>
              <strong style={{ color: 'var(--c-fg-default)', fontWeight: 600 }}>
                {m.author_handle ? `@${m.author_handle}` : 'unknown'}
              </strong>
              : {m.text || '(media)'}{' '}
              <span style={{ color: 'var(--c-fg-tertiary)' }}>· {parseServerDate(m.created_at).toLocaleString()}</span>
            </li>
          ))}
        </ul>
      ) : (
        <span>No messages at report time.</span>
      )}
    </div>
  );
}

function LaneButton({
  label,
  armedLabel,
  isArmed,
  onClick,
}: {
  label: string;
  armedLabel: string;
  isArmed: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '6px 12px',
        borderRadius: 6,
        border: isArmed ? '1px solid var(--c-fg-default)' : '1px solid var(--c-border-hairline)',
        background: isArmed ? 'var(--c-fg-default)' : 'var(--c-bg-surface)',
        color: isArmed ? 'var(--c-bg-page)' : 'var(--c-fg-default)',
        fontSize: 13,
        fontWeight: 600,
        cursor: 'pointer',
      }}
    >
      {isArmed ? armedLabel : label}
    </button>
  );
}

const MESSAGE_KIND_LABEL: Record<MessageReportKind, string> = {
  chat_message: 'Chat',
  channel_message: 'Channel',
  note_message: 'Notes',
};

/** Reported messages (0383): the snapshot taken at report time, the
 *  reporters, and Dismiss / Remove message / Suspend author. */
function MessageQueue() {
  const [status, setStatus] = useState<'open' | 'actioned' | 'dismissed' | 'all'>('open');
  const [rows, setRows] = useState<MessageQueueItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [armed, setArmed] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await moderationApi.messageQueue(status);
      setRows(res.queue);
    } catch {
      setError("Couldn't load the queue. Try again.");
    }
  }, [status]);

  useEffect(() => {
    setRows(null);
    setArmed(null);
    void load();
  }, [load]);

  const act = useCallback(
    async (key: string, fn: () => Promise<unknown>, done: string) => {
      if (armed !== key) {
        setArmed(key);
        return;
      }
      setArmed(null);
      setNote(null);
      try {
        await fn();
        setNote(done);
        void load();
      } catch {
        setNote('Action failed. Try again.');
      }
    },
    [armed, load],
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <label style={{ fontSize: 13, color: 'var(--c-fg-secondary)' }}>Status</label>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as typeof status)}
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--c-border-hairline)', background: 'var(--c-bg-surface)', color: 'var(--c-fg-default)', fontSize: 13 }}
        >
          <option value="open">Open</option>
          <option value="actioned">Actioned</option>
          <option value="dismissed">Dismissed</option>
          <option value="all">All</option>
        </select>
        {note ? <span style={{ fontSize: 13, color: 'var(--c-fg-secondary)' }}>{note}</span> : null}
      </div>
      {error ? (
        <p style={{ color: 'var(--c-fg-default)' }}>{error}</p>
      ) : rows === null ? (
        <p style={{ color: 'var(--c-fg-tertiary)', fontSize: 14 }}>Loading…</p>
      ) : rows.length === 0 ? (
        <p style={{ color: 'var(--c-fg-tertiary)', fontSize: 14 }}>No reported messages in this view.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {rows.map((r) => {
            const key = `${r.kind}:${r.target_id}`;
            const ev = r.evidence ?? {};
            const where =
              r.kind === 'channel_message'
                ? `#${ev.channel_name ?? '?'}`
                : r.kind === 'chat_message'
                  ? ev.conversation_kind === 'group'
                    ? `group "${ev.conversation_title ?? 'untitled'}"`
                    : 'one-to-one chat'
                  : 'Notes';
            return (
              <div key={key} style={{ border: '1px solid var(--c-border-hairline)', borderRadius: 8, background: 'var(--c-bg-surface)', padding: 14 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                  <strong style={{ fontSize: 14 }}>
                    {MESSAGE_KIND_LABEL[r.kind]} · @{ev.author_handle ?? ev.author_id ?? '?'} in {where}
                  </strong>
                  <span style={{ fontSize: 12, color: 'var(--c-fg-secondary)' }}>
                    {r.open_reports} report{r.open_reports === 1 ? '' : 's'} · {r.reasons.join(', ')}
                  </span>
                  {r.removed ? <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--c-state-danger)' }}>Removed</span> : null}
                </div>
                <blockquote style={{ margin: '10px 0', padding: '8px 12px', borderLeft: '3px solid var(--c-border-strong)', background: 'var(--c-bg-surface-alt)', whiteSpace: 'pre-wrap', fontSize: 14 }}>
                  {ev.text || (ev.media_count ? `(${ev.media_count} attachment${ev.media_count === 1 ? '' : 's'}, no text)` : '(no text)')}
                </blockquote>
                {ev.reporter_supplied ? (
                  <p style={{ margin: '0 0 8px', fontSize: 12, color: 'var(--c-fg-tertiary)' }}>
                    Notes are end-to-end encrypted: this is the text the reporter decrypted and chose to send.
                  </p>
                ) : null}
                <ul style={{ margin: '0 0 10px', paddingLeft: 18, fontSize: 13, color: 'var(--c-fg-secondary)' }}>
                  {r.reports.map((rep) => (
                    <li key={rep.id}>
                      @{rep.reporter_handle ?? '?'}: {rep.reason}
                      {rep.details ? ` (${rep.details})` : ''}
                    </li>
                  ))}
                </ul>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <LaneButton
                    label="Dismiss"
                    armedLabel="Click again to dismiss"
                    isArmed={armed === `dismiss:${key}`}
                    onClick={() => act(`dismiss:${key}`, () => moderationApi.resolveMessage(r.kind, r.target_id, 'dismissed'), 'Dismissed.')}
                  />
                  {!r.removed ? (
                    <LaneButton
                      label="Remove message"
                      armedLabel="Click again to remove for everyone"
                      isArmed={armed === `remove:${key}`}
                      onClick={() => act(`remove:${key}`, () => moderationApi.resolveMessage(r.kind, r.target_id, 'remove'), 'Message removed; reports actioned.')}
                    />
                  ) : null}
                  {ev.author_id ? (
                    <LaneButton
                      label="Suspend author 7d"
                      armedLabel="Click again to suspend 7 days"
                      isArmed={armed === `sus:${key}`}
                      onClick={() =>
                        act(
                          `sus:${key}`,
                          async () => {
                            await moderationApi.suspendUser(ev.author_id!, { days: 7, reason: `Reported message: ${r.reasons.join(', ')}` });
                            await moderationApi.resolveMessage(r.kind, r.target_id, 'remove');
                          },
                          'Author suspended 7 days; message removed.',
                        )
                      }
                    />
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const FEEDBACK_TOPIC_LABEL: Record<string, string> = {
  child_safety: 'Child safety concern',
  problem: 'Problem',
  suggestion: 'Suggestion',
  other: 'Something else',
};

/** In-app feedback (0505): one card per message, child safety first. */
function FeedbackQueue() {
  const [status, setStatus] = useState<'open' | 'actioned' | 'dismissed' | 'all'>('open');
  const [rows, setRows] = useState<FeedbackQueueItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [armed, setArmed] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await moderationApi.feedbackQueue(status);
      setRows(res.queue);
    } catch {
      setError("Couldn't load feedback. Try again.");
    }
  }, [status]);

  useEffect(() => {
    setRows(null);
    setArmed(null);
    void load();
  }, [load]);

  const act = useCallback(
    async (key: string, fn: () => Promise<unknown>, done: string) => {
      if (armed !== key) {
        setArmed(key);
        return;
      }
      setArmed(null);
      setNote(null);
      try {
        await fn();
        setNote(done);
        void load();
      } catch {
        setNote('Action failed. Try again.');
      }
    },
    [armed, load],
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <label style={{ fontSize: 13, color: 'var(--c-fg-secondary)' }}>Status</label>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as typeof status)}
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--c-border-hairline)', background: 'var(--c-bg-surface)', color: 'var(--c-fg-default)', fontSize: 13 }}
        >
          <option value="open">Open</option>
          <option value="actioned">Handled</option>
          <option value="dismissed">Closed</option>
          <option value="all">All</option>
        </select>
        {note ? <span style={{ fontSize: 13, color: 'var(--c-fg-secondary)' }}>{note}</span> : null}
      </div>
      {error ? (
        <p style={{ color: 'var(--c-fg-default)' }}>{error}</p>
      ) : rows === null ? (
        <p style={{ color: 'var(--c-fg-tertiary)', fontSize: 14 }}>Loading…</p>
      ) : rows.length === 0 ? (
        <p style={{ color: 'var(--c-fg-tertiary)', fontSize: 14 }}>No feedback in this view.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {rows.map((r) => {
            const urgent = r.topic === 'child_safety';
            const ev = r.evidence ?? {};
            const open = r.status === 'open' || r.status === 'reviewing';
            return (
              <div
                key={r.id}
                style={{
                  border: `1px solid ${urgent ? 'var(--c-accent-default)' : 'var(--c-border-hairline)'}`,
                  borderRadius: 8,
                  background: 'var(--c-bg-surface)',
                  padding: 14,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                  <strong style={{ fontSize: 14 }}>{FEEDBACK_TOPIC_LABEL[r.topic] ?? r.topic}</strong>
                  {urgent ? (
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--c-accent-default)' }}>URGENT</span>
                  ) : null}
                  <span style={{ fontSize: 12, color: 'var(--c-fg-secondary)' }}>
                    from @{r.reporter_handle ?? r.reporter_id ?? '?'} · {parseServerDate(r.created_at).toLocaleString()}
                    {ev.platform ? ` · ${ev.platform}${ev.app_version ? ` ${ev.app_version}` : ''}` : ''}
                  </span>
                </div>
                <blockquote style={{ margin: '10px 0', padding: '8px 12px', borderLeft: '3px solid var(--c-border-strong)', background: 'var(--c-bg-surface-alt)', whiteSpace: 'pre-wrap', fontSize: 14 }}>
                  {r.message || '(no text)'}
                </blockquote>
                {ev.involved ? (
                  <p style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--c-fg-secondary)' }}>
                    Involved: <span style={{ color: 'var(--c-fg-default)' }}>{ev.involved}</span>
                  </p>
                ) : null}
                {r.resolution_note ? (
                  <p style={{ margin: '0 0 10px', fontSize: 12, color: 'var(--c-fg-tertiary)' }}>{r.resolution_note}</p>
                ) : null}
                {open ? (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <LaneButton
                      label="Mark handled"
                      armedLabel="Click again to mark handled"
                      isArmed={armed === `act:${r.id}`}
                      onClick={() => act(`act:${r.id}`, () => moderationApi.resolveFeedback(r.id, 'actioned'), 'Marked handled.')}
                    />
                    <LaneButton
                      label="Close"
                      armedLabel="Click again to close"
                      isArmed={armed === `dis:${r.id}`}
                      onClick={() => act(`dis:${r.id}`, () => moderationApi.resolveFeedback(r.id, 'dismissed'), 'Closed.')}
                    />
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
